import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { commonMessages } from 'src/utils/messages';
import InputFormField from 'src/registry/components/forms/InputFormField';
import { Input } from 'src/registry/ui/Input';
import React from 'react';
import { FinancialDocumentRowFormSchemaValues } from 'src/features/financialDocumentRows/utils/formSchema';
import { financialDocumentRowMessages } from 'src/features/financialDocumentRows/utils/messages';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { accountingHeadersMessages } from 'src/features/accountingHeaders/utils/messages';
import { useListAccountingHeadings } from 'src/features/accountingHeaders/api/useListAccountingHeadings';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';
import { useListUnits } from 'src/features/units/api/useListUnits';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { unitsMessages } from 'src/features/units/utils/messages';
import { useListTaxReasons } from 'src/features/taxReasons/api/useListTaxReasons';
import { taxReasonsMessages } from 'src/features/taxReasons/utils/messages';
import { useGetTaxReason } from 'src/features/taxReasons/api/useGetTaxReason';

type FinancialDocumentRowFormProps = {
    form: UseFormReturn<FinancialDocumentRowFormSchemaValues>;
    onSubmit: (values: FinancialDocumentRowFormSchemaValues) => void;
};

function FinancialDocumentRowForm({ onSubmit, form }: FinancialDocumentRowFormProps) {
    const { formatMessage } = useIntl();
    const [searchAccountingHeadings, setSearchAccountingHeadings] = React.useState<string>('');

    const { tax_reason, price, tax_percent } = form.watch();

    const [priceWithTax, setPriceWithTax] = React.useState<string>('');
    const [displayPrice, setDisplayPrice] = React.useState<string>('');

    const { data: accountingHeadings } = useListAccountingHeadings({
        search: searchAccountingHeadings,
    });
    const { data: units } = useListUnits({});

    const { data: taxReasons } = useListTaxReasons({
        is_active: true,
    });

    const { data: selectedTaxReason } = useGetTaxReason({
        id: tax_reason,
        config: { enabled: !!tax_reason },
    });

    const [taxReasonPercent, setTaxReasonPercent] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        if (!selectedTaxReason?.value) return;

        setTaxReasonPercent(selectedTaxReason.value);
        form.setValue('tax_percent', selectedTaxReason?.value);
    }, [form, selectedTaxReason, tax_percent, tax_reason]);

    React.useEffect(() => {
        if (price == null || taxReasonPercent == null) return;

        const newValue = Math.round(price * (1 + Number(taxReasonPercent)));

        form.setValue('price_with_tax', newValue, {
            shouldDirty: true,
            shouldValidate: true,
        });

        setPriceWithTax((newValue / 100).toFixed(2));
    }, [form, price, taxReasonPercent]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<FinancialDocumentRowFormSchemaValues>
                form={form}
                name="number"
                type="number"
                label={formatMessage(financialDocumentRowMessages.number)}
            />
            <InputFormField<FinancialDocumentRowFormSchemaValues>
                form={form}
                name="name"
                type="text"
                label={formatMessage(commonMessages.name)}
            />
            <InputFormField<FinancialDocumentRowFormSchemaValues>
                form={form}
                name="quantity"
                type="number"
                label={formatMessage(financialDocumentRowMessages.quantity)}
            />
            <FormField
                control={form.control}
                name="tax_reason"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...taxReasonsMessages.taxReason} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(taxReasonsMessages.taxReason)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {taxReasons?.results?.map((taxReason) => (
                                            <SelectItem key={taxReason.id} value={taxReason.id}>
                                                {`${taxReason.name} - ${(Number(taxReason.value) * 100).toFixed(2)}%`}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="tax_percent"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...financialDocumentRowMessages.taxPercent} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    value={taxReasonPercent ? (Number(taxReasonPercent) * 100).toFixed(2) : undefined}
                                    placeholder={formatMessage(financialDocumentRowMessages.taxPercent)}
                                    disabled
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...financialDocumentsMessages.price} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    value={
                                        displayPrice !== ''
                                            ? displayPrice
                                            : field.value != null
                                              ? (field.value / 100).toFixed(2)
                                              : ''
                                    }
                                    placeholder={`${formatMessage(financialDocumentsMessages.price)}...`}
                                    onChange={(e) => {
                                        const input = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(input)) return;

                                        setDisplayPrice(input);

                                        if (input === '') {
                                            field.onChange(undefined);
                                            form.setValue('price_with_tax', undefined);
                                            setPriceWithTax('');
                                            return;
                                        }

                                        if (input.endsWith('.')) {
                                            return;
                                        }

                                        const numeric = parseFloat(input);
                                        if (isNaN(numeric)) return;

                                        const percent = Number(taxReasonPercent);

                                        const priceCents = Math.round(numeric * 100);
                                        const priceWithTaxCents = Math.round(priceCents * (1 + percent));

                                        field.onChange(priceCents);
                                        form.setValue('price_with_tax', priceWithTaxCents);

                                        setPriceWithTax((priceWithTaxCents / 100).toFixed(2));
                                    }}
                                    onBlur={() => {
                                        if (displayPrice === '') return;
                                        const numeric = parseFloat(displayPrice);
                                        if (!isNaN(numeric)) {
                                            setDisplayPrice(numeric.toFixed(2));
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="price_with_tax"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...financialDocumentsMessages.priceWithTax} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(financialDocumentsMessages.priceWithTax)}...`}
                                    value={
                                        priceWithTax !== ''
                                            ? priceWithTax
                                            : field.value != null
                                              ? (field.value / 100).toFixed(2)
                                              : ''
                                    }
                                    onChange={(e) => {
                                        const input = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(input)) return;

                                        setPriceWithTax(input);

                                        if (input === '') {
                                            field.onChange(undefined);
                                            // form.setValue('price', undefined);
                                            setDisplayPrice('');
                                            return;
                                        }

                                        if (input.endsWith('.')) {
                                            return;
                                        }

                                        const numeric = parseFloat(input);

                                        if (isNaN(numeric)) return;

                                        const percent = Number(taxReasonPercent); // 0.25

                                        const priceWithTaxCents = Math.round(numeric * 100);
                                        const priceCents = Math.round(priceWithTaxCents / (1 + percent));

                                        field.onChange(priceWithTaxCents);
                                        form.setValue('price', priceCents);

                                        setDisplayPrice((priceCents / 100).toFixed(2));
                                    }}
                                    onBlur={() => {
                                        if (priceWithTax === '') return;
                                        const numeric = parseFloat(priceWithTax);
                                        if (!isNaN(numeric)) {
                                            setPriceWithTax(numeric.toFixed(2));
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="unit"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...unitsMessages.unit} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(unitsMessages.unit)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {units?.results?.map((unit) => (
                                            <SelectItem key={unit.id} value={unit.id}>
                                                {unit.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SearchSelectFormField
                name="accounting_heading"
                control={form.control}
                label={formatMessage(accountingHeadersMessages.header)}
                items={accountingHeadings?.results ?? []}
                clearable
                placeholder={`${formatMessage(accountingHeadersMessages.header)}...`}
                searchPlaceholder={formatMessage(accountingHeadersMessages.searchAccountingHeader)}
                emptyTitle={formatMessage(accountingHeadersMessages.noAccountingHeader)}
                emptyDescription={formatMessage(accountingHeadersMessages.noAccountingHeaderFound)}
                getItemValue={(h) => h.id}
                getItemLabel={(h) => h.name}
                getItemKeywords={(h) => [h.name]}
                loadOptions={setSearchAccountingHeadings}
            />
        </Form>
    );
}

export default FinancialDocumentRowForm;
